// import '@babel/polyfill';
import Vue from 'vue';
import { ContentLoader } from 'vue-content-loader';
import VueRouter from 'vue-router';
import axios from 'axios';

// import 'intersection-observer';
// import 'dom-node-polyfills';
// import 'url-search-params-polyfill';
import 'font-awesome/css/font-awesome.min.css';
import 'material-design-icons-iconfont/dist/material-design-icons.css';
// import cssVars from 'css-vars-ponyfill';
import './assets/style.styl';

import mixins from './mixins/mixins';
import mainStore from './components/store/mainStore';
import ApplicationContainer from './ApplicationContainer';

Vue.component('ApplicationContainer', ApplicationContainer);
Vue.component('content-loader', ContentLoader);

Vue.config.productionTip = false;

Vue.$prepareComponentInstance = executable => {
    try {
        const invokeResult = executable();
        if (invokeResult instanceof Promise) {
            return invokeResult.then(component => {
                return component.default ? component.default : component;
            });
        } else {
            return Promise.resolve(executable);
        }
    } catch (err) {
        return Promise.resolve(executable);
    }
};

export default {
    mixins,
    el: '#application-container',
    store: mainStore,
    metaInfo: {
        title: 'Prime',
        titleTemplate: '%s'
    },
    data: {
        displayDesktopNav: false,
        displayMobileNav: false,
        initialLayoutLoaded: false
    },
    render(h) {
        const unwatch = this.$store.watch(
            () => this.$store.state.Layout.currentLayout,
            () => {
                this.initialLayoutLoaded = true;
                unwatch();
            }
        );

        if (this.initialLayoutLoaded) {
            return h('application-container');
        } else {
            return h('div', {
                domProps: {
                    innerHTML: document.getElementById('application-container')
                        .innerHTML
                }
            });
        }
    },
    async prepareInstance() {
        const me = this;
        const bootstrapComponents = Object.values(this.bootstrapComponents);
        bootstrapComponents.sort((a, b) => {
            let aPriority = a.priority ? a.priority : 0;
            let bPriority = b.priority ? b.priority : 0;
            if (aPriority === bPriority) {
                return 0;
            }
            if (aPriority === null || aPriority < bPriority) {
                return -1;
            }
            if (bPriority === null || aPriority > bPriority) {
                return 1;
            }
        });
        for (const bootstrapComponent of bootstrapComponents) {
            await this.installComponent(bootstrapComponent);
        }
        const simpleComponentNames = Object.keys(this.simpleComponents);
        for (const simpleComponentName of simpleComponentNames) {
            await me.addComponent(
                simpleComponentName,
                this.simpleComponents[simpleComponentName]
            );
        }
        await this.setupAxios();
        const routes = Object.values(this.routerConfig.routes);
        routes.sort((a, b) => {
            let aPriority = null;
            let bPriority = null;
            if (a.hasOwnProperty('meta') && a.meta.hasOwnProperty('priority')) {
                aPriority = a.meta.priority;
            }
            if (b.hasOwnProperty('meta') && b.meta.hasOwnProperty('priority')) {
                bPriority = b.meta.priority;
            }
            if (aPriority === bPriority) {
                return 0;
            }
            if (aPriority === null || aPriority < bPriority) {
                return -1;
            }
            if (bPriority === null || aPriority > bPriority) {
                return 1;
            }
        });

        const { isNavigationFailure } = VueRouter;

        const originalPush = VueRouter.prototype.push;
        VueRouter.prototype.push = function push(
            location,
            onComplete,
            onAbort
        ) {
            const result = originalPush.call(
                this,
                location,
                onComplete,
                onAbort
            );
            if (result) {
                return result.catch(err => {
                    if (!isNavigationFailure(err)) {
                        throw err;
                    }
                });
            }
            return result;
        };

        const originalReplace = VueRouter.prototype.replace;
        VueRouter.prototype.replace = function replace(
            location,
            onComplete,
            onAbort
        ) {
            const result = originalReplace.call(
                this,
                location,
                onComplete,
                onAbort
            );
            if (result) {
                return result.catch(err => {
                    if (!isNavigationFailure(err)) {
                        throw err;
                    }
                });
            }
            return result;
        };
        const router = new VueRouter({
            mode: 'history',
            routes: routes,
            scrollBehavior() {
                return {
                    x: 0,
                    y: 0
                };
            }
        });
        this.router = router;
        Vue.$router = router;
        Vue.use(VueRouter);

        const plugins = Object.values(this.plugins);
        plugins.sort((a, b) => {
            let aPriority = a.priority;
            let bPriority = b.priority;
            if (aPriority === bPriority) {
                return 0;
            }
            if (aPriority === null || aPriority < bPriority) {
                return -1;
            }
            if (bPriority === null || aPriority > bPriority) {
                return 1;
            }
        });
        for (const plugin of plugins) {
            await this.installComponent(plugin);
        }
        const payments = Object.values(this.payments);
        for (const payment of payments) {
            await this.installComponent(payment);
        }
    },
    getComponentInstance(component) {
        if (component.default) {
            component = component.default;
        } else if (typeof component.hasOwnProperty === 'function') {
            component = component.hasOwnProperty('default')
                ? component.default
                : component;
        }
        return component;
    },
    addComponent(componentName, componentInstance) {
        Vue.component(componentName, componentInstance);
    },
    async installComponent(installableComponent) {
        const componentInstance = installableComponent.instance;
        const invokeResult = await componentInstance();
        const component = this.getComponentInstance(invokeResult);
        const options = installableComponent.hasOwnProperty('options')
            ? installableComponent.options
            : {};
        await Vue.use(component, options);
        if (typeof installableComponent.callback === 'function') {
            installableComponent.callback();
        }
    },
    bootstrapComponents: {
        vuetify: {
            instance: () => import(/* webpackChunkName: "default" */ 'vuetify'),
            options: {}
        },
        graphql: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-graphql'
                ),
            options: {}
        },
        localStorage: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-privacy/src/plugin/localStorage'
                ),
            options: {},
            priority: 1
        },
        sessionStorage: {
            instance: () =>
                import(/* webpackChunkName: "default" */ 'vue-sessionstorage'),
            options: {},
            priority: 1,
            callback() {
                Vue.$session = Vue.prototype.$session;
            }
        },
        lazyLoad: {
            instance: () =>
                import(/* webpackChunkName: "default" */ 'vue-lazyload'),
            options: {
                error: '/images/no-available-image.png'
            }
        },
        meta: {
            instance: () =>
                import(/* webpackChunkName: "default" */ 'vue-meta'),
            options: {}
        }
    },
    plugins: {
        config: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ './plugin/ConfigPlugin'
                ),
            options: {},
            priority: 0
        },
        language: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-i18n/src/plugin/language'
                ),
            options: {},
            priority: 0
        },
        countryDetectService: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-country/src/plugin/DetectCountryBy/browserLocale'
                ),
            options: {},
            priority: 0
        },
        country: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-country/src/plugin/country'
                ),
            options: {},
            priority: 1
        },
        channel: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-country/src/plugin/channel'
                ),
            options: {},
            priority: 1
        },
        currency: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-product/src/plugin/currency'
                ),
            options: {
                name: 'vueCurrencyFilter',
                thousandsSeparator: '.',
                fractionCount: 2,
                fractionSeparator: ',',
                symbolPosition: 'front',
                symbolSpacing: true
            },
            priority: 0
        },
        customer: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-customer/src/plugin/customer'
                ),
            options: {},
            priority: 10
        },
        coupon: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-coupon/src/plugin/coupon'
                ),
            options: {},
            priority: 100
        },
        order: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-checkout/src/plugin/order'
                ),
            options: {},
            priority: 100
        },
        partner: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-checkout/src/plugin/partner'
                ),
            options: {},
            priority: 110
        },
        searchFilters: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-search/src/plugin/searchFilters'
                ),
            options: {
                searchResultGridClasses: 'xs12 sm6 md4'
            },
            priority: 5
        },
        search: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-search/src/plugin/search'
                ),
            options: {
                searchResultGridClasses: 'xs12 sm6 md4'
            },
            priority: 100
        },
        page: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-page/src/plugin/page'
                ),
            options: {},
            priority: 10
        },
        seoUrl: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-page/src/plugin/seo-url'
                ),
            options: {},
            priority: 0
        },
        product: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-product/src/plugin'
                ),
            options: {},
            priority: 0
        },
        basket: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-basket/src/plugin/basket'
                ),
            options: {},
            priority: 100
        },
        layout: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-layout/src/plugin/layout'
                ),
            options: {},
            priority: 0
        },
        payment: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-payment/src/plugin'
                ),
            options: {},
            priority: 0
        },
        client: {
            instance: () =>
                import(/* webpackChunkName: "default" */ './plugin'),
            options: {},
            priority: 0
        },
        wishlist: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-wishlist/src/plugin/wishlist'
                ),
            options: {},
            priority: 100
        }
    },
    simpleComponents: {
        'basket-item': () =>
            import(
                /* webpackChunkName: "basket" */ '@breakmedia/prime-vue-basket/src/BasketItem'
            ),
        'basket-items': () =>
            import(
                /* webpackChunkName: "basket" */ '@breakmedia/prime-vue-basket/src/BasketItems'
            ),
        'basket-amount': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-basket/src/BasketAmount'
            ),
        'basket-totals': () =>
            import(
                /* webpackChunkName: "basket" */ '@breakmedia/prime-vue-basket/src/BasketTotals'
            ),
        'free-shipping-info': () =>
            import(
                /* webpackChunkName: "basket" */ '@breakmedia/prime-vue-basket/src/FreeShippingInfo'
            ),
        coupon: () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-coupon/src/Coupon'
            ),
        'floating-basket': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-basket/src/FloatingBasket/FloatingBasket'
            ),
        'added-to-basket': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-basket/src/FloatingBasket/AddedToBasket'
            ),
        'shipping-selector': () =>
            import(
                /* webpackChunkName: "basket" */ '@breakmedia/prime-vue-basket/src/ShippingSelector'
            ),
        'default-layout': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-layout/src/DefaultLayout'
            ),
        'page-modal': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-page/src/PageModal'
            ),
        'static-page-module': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-page/src/StaticPageModule'
            ),
        'prime-carousel': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-page/src/PrimeCarousel'
            ),
        'contact-form': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-contact/src/contact-form/ContactForm'
            ),
        'country-switcher': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-country/src/CountrySwitcher/CountrySwitcher'
            ),
        'country-image': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-country/src/CountryImage/CountryImage'
            ),
        'login-form': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-customer/src/Login/LoginForm'
            ),
        'login-modal': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-customer/src/Login/LoginModal'
            ),
        'account-navigation': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-customer/src/AccountNavigation'
            ),
        'product-thumbnail': () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/ProductThumbnail'
            ),
        'product-thumbnail-small': () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/ProductThumbnailSmall'
            ),
        'related-products-thumbnail': () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/product-page/RelatedProductsThumbnail'
            ),
        'product-sidebar': () => ({
            //Due to strange bugs in working of vue slots with async components - an empty loading components is used
            component: import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/product-page/ProductSidebar'
            ),
            loading: { template: '' }
        }),
        'merchants-offers': () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/product-page/MerchantsOffers'
            ),
        'product-title': () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/product-page/ProductTitle'
            ),
        'product-sub-title': () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/product-page/ProductSubTitle'
            ),
        'images-and-videos': () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/product-page/ImagesAndVideos'
            ),
        'product-short-description': () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/product-page/ProductShortDescription'
            ),
        'product-price': () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/product-page/ProductPrice'
            ),
        upsells: () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/product-page/RelatedProducts'
            ),
        crosssells: () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/product-page/RelatedProducts'
            ),
        'average-rating': () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/product-page/AverageRating'
            ),
        'product-reviews': () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/product-page/ProductReviews'
            ),
        'products-list': () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/ProductsList'
            ),
        'notification-modal': () =>
            import(
                /* webpackChunkName: "default" */ './components/NotificationModal/NotificationModal.vue'
            ),
        'language-switcher': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-i18n/src/LanguageSwitcher/LanguageSwitcher'
            ),
        payment: () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-payment/src/Payment'
            ),
        'search-results-template': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-search/src/SearchResultsTemplate'
            ),
        'search-results-placeholder': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-search/src/SearchResultsPlaceholder'
            ),
        'desktop-filters': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-search/src/DesktopFilters'
            ),
        'mobile-filters': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-search/src/MobileFilters'
            ),
        'search-filter-btn': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-search/src/SearchFilterBtn'
            ),
        'search-box': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-search/src/SearchBox'
            ),
        'search-box-mobile': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-search/src/SearchBox'
            ),
        stepper: () =>
            import(
                /* webpackChunkName: "stepper" */ '@breakmedia/prime-vue-stepper/src/StepperBase'
            ),
        'wishlist-button': () =>
            import(
                /* webpackChunkName: "wishlist" */ '@breakmedia/prime-vue-wishlist/src/WishlistButton'
            ),
        'page-content': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-page/src/PageContent'
            ),
        'page-not-found': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-page/src/PageNotFound'
            ),
        'card-group': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-page/src/page-components/card/CardGroup'
            ),
        'summary-step': () =>
            import(
                /* webpackChunkName: "checkout" */ '@breakmedia/prime-vue-checkout/src/SummaryStep/SummaryStep'
            ),
        'success-step': () =>
            import(
                /* webpackChunkName: "checkout" */ '@breakmedia/prime-vue-checkout/src/SuccessStep/SuccessStep'
            ),
        'address-form': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-checkout/src/AddressStep/AddressForm'
            ),
        'registration-success-modal': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-customer/src/Registration/RegistrationSuccessModal'
            ),
        'currency-switcher': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-product/src/components/CurrencySwitcher'
            ),
        'tier-prices': () =>
            import(
                /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/components/TierPrices'
            ),
        'channel-switcher': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-country/src/ChannelSwitcher/ChannelSwitcher'
            ),
        'info-bar': () =>
            import(
                /* webpackChunkName: "default" */ '@breakmedia/prime-vue-page/src/page-components/info/InfoBar'
            ),
        'progress-bar': () =>
            import(/* webpackChunkName: "default" */ './components/ProgressBar')
    },
    payments: {
        paypalPaymentType: {
            instance: () =>
                import(
                    /* webpackChunkName: "default" */ '@breakmedia/prime-vue-payment-paypal/src/PaypalPaymentType'
                ),
            options: {}
        }
    },
    routerConfig: {
        routes: {
            search: {
                path: '/search/:search+',
                name: 'search',
                component: () =>
                    import(
                        /* webpackChunkName: "default" */ '@breakmedia/prime-vue-search/src/SearchResults'
                    ),
                meta: {
                    metaInfoProvider: true,
                    isSearchRoute: true,
                    resource: '*/search/index',
                    priority: 1
                }
            },
            merchantSearch: {
                path: '/:merchantUrl/search/:search*',
                name: 'merchant-search',
                component: () =>
                    import(
                        /* webpackChunkName: "default" */ '@breakmedia/prime-vue-search/src/SearchResults'
                    ),
                meta: {
                    metaInfoProvider: true,
                    isSearchRoute: true,
                    resource: '*/search/index',
                    priority: 1
                }
            },
            additionalMerchantSearch: {
                path: '/haendler/:merchantUrl/search/:search*',
                name: 'additional-merchant-search',
                component: () =>
                    import(
                        /* webpackChunkName: "default" */ '@breakmedia/prime-vue-search/src/SearchResults'
                    ),
                meta: {
                    metaInfoProvider: true,
                    isSearchRoute: true,
                    resource: '*/search/index',
                    priority: 1
                }
            },
            merchantProductPage: {
                path: '/:merchantUrl/product/:id',
                name: 'merchant-product',
                component: () =>
                    import(
                        /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/ProductPage'
                    ),
                meta: {
                    metaInfoProvider: true,
                    resource: '*/product/show',
                    priority: 1
                }
            },
            customerRegistration: {
                path: '/customer/registration',
                name: 'customer-registration',
                component: () =>
                    import(
                        /* webpackChunkName: "default" */ '@breakmedia/prime-vue-customer/src/CustomerRegistration'
                    ),
                meta: {
                    resource: '*/customer/registration',
                    priority: 1
                }
            },
            wishlist: {
                path: '/customer/wishlist',
                name: 'customer-wishlist',
                component: () =>
                    import(
                        /* webpackChunkName: "default" */ '@breakmedia/prime-vue-wishlist/src/Wishlist'
                    ),
                meta: {
                    resource: '*/customer/get_wishlist',
                    priority: 1
                }
            },
            customerOrders: {
                path: '/customer/orders',
                name: 'customer-orders',
                component: () =>
                    import(
                        /* webpackChunkName: "default" */ '@breakmedia/prime-vue-customer/src/profile/orders/Orders'
                    ),
                meta: {
                    resource: '*/customer/customer-orders',
                    priority: 1
                }
            },
            customerProfile: {
                path: '/customer/profile',
                name: 'customer-profile',
                component: () =>
                    import(
                        /* webpackChunkName: "default" */ '@breakmedia/prime-vue-customer/src/Profile'
                    ),
                meta: {
                    resource: '*/customer/customer-orders',
                    priority: 1
                }
            },
            confirmRegistration: {
                path: '/customer/confirm-registration/hash/:hash',
                name: 'confirm-registration',
                component: () =>
                    import(
                        /* webpackChunkName: "default" */ '@breakmedia/prime-vue-customer/src/Login/ConfirmRegistration/ConfirmRegistration'
                    ),
                meta: {
                    resource: '*/customer/confirm-registration'
                }
            },
            basket: {
                path: '/basket',
                name: 'basket',
                component: () =>
                    import(
                        /* webpackChunkName: "basket" */ '@breakmedia/prime-vue-basket/src/Basket'
                    ),
                meta: {
                    metaInfoProvider: true,
                    resource: '*/basket/index',
                    priority: 1
                }
            },
            basketMerge: {
                path: '/basket/merge',
                name: 'basket-merge',
                component: () =>
                    import(
                        /* webpackChunkName: "basket" */ '@breakmedia/prime-vue-basket/src/MergeBaskets'
                    ),
                meta: {
                    metaInfoProvider: true,
                    resource: '*/basket/index',
                    priority: 1
                }
            },
            payment: {
                path: '/checkout/:stepName(payment-processing)',
                name: 'payment-processing',
                component: () =>
                    import(
                        /* webpackChunkName: "checkout" */ '@breakmedia/prime-vue-checkout/src/Checkout'
                    ),
                meta: {
                    metaInfoProvider: true,
                    resource: '*/order-process',
                    priority: 1,
                    preventReferrerChange: true
                },
                props: {
                    hideHeader: true
                }
            },
            checkout: {
                path: '/checkout/:stepName?/:subStepName?',
                name: 'checkout',
                component: () =>
                    import(
                        /* webpackChunkName: "checkout" */ '@breakmedia/prime-vue-checkout/src/Checkout'
                    ),
                meta: {
                    metaInfoProvider: true,
                    resource: '*/order-process',
                    priority: 1,
                    preventReferrerChange: true
                }
            },
            catalogList: {
                path: '/catalog/page/:page?',
                name: 'catalog-list-page',
                component: () =>
                    import(
                        /* webpackChunkName: "catalog" */ '@breakmedia/prime-vue-catalog/src/pages/CatalogList'
                    ),
                meta: {
                    type: 'catalog',
                    resource: '*/catalog',
                    metaInfoProvider: true,
                    priority: 1
                }
            },
            catalog: {
                path: '/catalog/:id',
                name: 'catalog-detail-page',
                component: () =>
                    import(
                        /* webpackChunkName: "catalog" */ '@breakmedia/prime-vue-catalog/src/pages/CatalogDetail'
                    ),
                meta: {
                    resource: '*/catalog/show',
                    metaInfoProvider: true,
                    priority: 1
                }
            },
            home: {
                path: '/',
                name: 'home',
                meta: {
                    priority: 100
                },
                component: () =>
                    import(
                        /* webpackChunkName: "default" */ '@breakmedia/prime-vue-page/src/PageContent'
                    )
            },
            productPage: {
                path: '/:product?',
                name: 'product',
                component: () =>
                    import(
                        /* webpackChunkName: "product" */ '@breakmedia/prime-vue-product/src/ProductPage'
                    ),
                meta: {
                    metaInfoProvider: true,
                    resource: '*/product/show',
                    priority: 99,
                    template: 'productpagetemplate.html'
                }
            },
            default: {
                path: '/:page?',
                name: 'default',
                meta: {
                    priority: 100
                },
                component: () =>
                    import(
                        /* webpackChunkName: "default" */ '@breakmedia/prime-vue-page/src/PageContent'
                    )
            }
        }
    },
    setupAxios: () => {
        axios.interceptors.request.use(
            function(config) {
                let authToken = Vue.$store.state.Login.authToken;
                if (
                    !axios.defaults.headers.common.hasOwnProperty(
                        'Authorization'
                    ) &&
                    authToken
                ) {
                    config.headers.Authorization = 'Bearer ' + authToken;
                } else if (!authToken) {
                    delete config.headers.Authorization;
                }
                return config;
            },
            function(error) {
                return Promise.reject(error);
            }
        );
        axios.interceptors.response.use(
            function(response) {
                // Do something with response data
                return response;
            },
            function(error) {
                return new Promise((resolve, reject) => {
                    if (
                        error.config.hasOwnProperty('forceAuth') &&
                        error.config.forceAuth === true &&
                        (error.request.status === 403 ||
                            error.request.status === 401)
                    ) {
                        Vue.$store.commit('Login/setAuthToken', null);
                        Vue.$store.commit('Login/setShowLoginModal', true);
                        const loop = setInterval(() => {
                            if (Vue.$store.state.Login.authToken) {
                                clearInterval(loop);
                                resolve(axios.request(error.config));
                            }
                        }, 100);
                    } else {
                        reject(error);
                    }
                });
            }
        );
    }
};
